import {
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { paymentAllocationMessages } from 'src/features/paymentAllocations/utils/messages';
import useSorting from 'src/registry/hooks/useSorting';
import { useListPaymentAllocations } from 'src/features/paymentAllocations/api/useListPaymentAllocations';
import { PaymentAllocation } from 'src/features/paymentAllocations/utils/types';
import { paymentsMessages } from 'src/features/payments/utils/messages';
import CellPaymentAllocationActions from 'src/features/paymentAllocations/components/tables/CellPaymentAllocationActions';
import { useParams } from 'react-router-dom';
import { commonMessages } from 'src/utils/messages';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import CreatePaymentAllocationFormModal from 'src/features/paymentAllocations/components/forms/CreatePaymentAllocationFormModal';
import { PAYMENT_TYPE_TRANSLATION_MAP } from 'src/features/payments/utils/constants';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { CURRENCY } from 'src/types';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

type PaymentAllocationsTableProps = {
    disableCreateButton?: boolean;
    currency?: CURRENCY;
};

const PaymentAllocationsTable = ({ disableCreateButton = false, currency }: PaymentAllocationsTableProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const { paymentId = '', financialDocumentId = '' } = useParams();

    const permissionManagePayments = useHasPermissions([PERMISSION.MANAGE_PAYMENTS]);
    const permissionAccessFinancialDocs = useHasPermissions([PERMISSION.ACCESS_FINANCIAL_DOCUMENTS]);
    const permissionAccessPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: true }],
    });

    const {
        data: paymentAllocations,
        isLoading,
        isError,
    } = useListPaymentAllocations({
        ...(paymentId && { payment: paymentId }),
        ...(financialDocumentId && { financial_document: financialDocumentId }),
        ...querySearchOptions,
        ...querySortingOptions,
    });

    const isPaymentDashboard = !!paymentId;

    const columnHelper = createColumnHelper<PaymentAllocation>();

    const columns = [
        ...(isPaymentDashboard
            ? [
                  columnHelper.accessor('financial_document.number', {
                      enableSorting: true,
                      header: DataTableColumnHeader,
                      cell: (ctx) => {
                          const financialDocumentId = ctx.row.original.financial_document?.id;
                          return (
                              <CellLinkWithIcon
                                  {...ctx}
                                  to={`/financial-documents/${financialDocumentId}`}
                                  hasPermissions={permissionAccessFinancialDocs}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(financialDocumentsMessages.financialDocument),
                      },
                  }),
                  columnHelper.accessor('created_at', {
                      header: DataTableColumnHeader,
                      cell: CellDatetime,
                      enableSorting: true,
                      meta: {
                          title: formatMessage(commonMessages.date),
                      },
                  }),
                  columnHelper.accessor('financial_document.currency', {
                      enableSorting: true,
                      header: DataTableColumnHeader,
                      cell: ({ row }) => {
                          return <CellCurrencyBadge currency={row.original.financial_document.currency} />;
                      },
                      meta: {
                          title: formatMessage(commonMessages.currency),
                      },
                  }),
              ]
            : []),

        ...(!isPaymentDashboard
            ? [
                  columnHelper.accessor('payment.date', {
                      enableSorting: true,
                      header: DataTableColumnHeader,
                      cell: (ctx) => {
                          const payment = ctx.row.original.payment;
                          const translatedPaymentType = formatMessage(PAYMENT_TYPE_TRANSLATION_MAP[payment.type]);
                          const paymentDate = getFormattedDateTimeForDetailPanel(payment.date);

                          const paymentId = ctx.row.original.payment?.id;
                          return (
                              <CellLinkWithIcon
                                  {...ctx}
                                  to={`/payments/${paymentId}`}
                                  render={() => `${paymentDate}-${translatedPaymentType}`}
                                  hasPermissions={permissionAccessPayments}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(paymentsMessages.payment),
                      },
                  }),
                  columnHelper.accessor('created_at', {
                      header: DataTableColumnHeader,
                      cell: CellDatetime,
                      enableSorting: true,
                      meta: {
                          title: formatMessage(commonMessages.date),
                      },
                  }),
                  columnHelper.accessor('payment.currency', {
                      enableSorting: true,
                      header: DataTableColumnHeader,
                      cell: ({ row }) => {
                          return <CellCurrencyBadge currency={row.original.payment.currency} />;
                      },
                      meta: {
                          title: formatMessage(commonMessages.currency),
                      },
                  }),
              ]
            : []),

        columnHelper.accessor('amount', {
            enableSorting: true,
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return formatNumber(row.original.amount / 100, {
                    style: 'currency',
                    currency: currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        ...(permissionManagePayments
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellPaymentAllocationActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable
            columns={columns}
            data={paymentAllocations}
            semantics={{
                title: formatMessage(paymentAllocationMessages.paymentAllocations),
                entryName: formatMessage(paymentAllocationMessages.allocation).toLowerCase(),
                entryNames: formatMessage(paymentAllocationMessages.allocations).toLowerCase(),
                placeholder: formatMessage(paymentAllocationMessages.allocation).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry:
                    permissionManagePayments && !disableCreateButton ? CreatePaymentAllocationFormModal : undefined,
            }}
        />
    );
};

export default PaymentAllocationsTable;
